import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { cloneDeep } from 'lodash';

export type HistoryChangeType = 'add' | 'undo' | 'redo';
export interface HistoryChangeEvent<T> {
    currentValue: T;
    canUndo: boolean;
    canRedo: boolean;
    index: number;
    type: HistoryChangeType;
}

@Injectable()
export class HistoryService<T> {
    private limit = 10;

    history: T[] = [];
    index = -1; // no items in history yet, so no index yet

    onChangeSubject = new Subject<HistoryChangeEvent<T>>();
    onChange$: Observable<HistoryChangeEvent<T>> = this.onChangeSubject.asObservable();

    setLimit(limit: number) {
        this.limit = limit;
    }

    init(item: T) {
        this.add(item, true);
    }

    add(item: T, init?: boolean) {
        this.index++;

        // if we add a new item, but the index has moved down we should remove all
        // items after the index and the current index becomes the last added
        if (this.index < this.limit - 1) {
            const removableAmount = this.limit - this.index;
            this.history.splice(this.limit - removableAmount, removableAmount);
        }

        this.history.push(cloneDeep(item));

        // remove first item when the limit is reached
        if (this.history.length - 1 === this.limit) {
            this.history.shift();
        }

        if (!init) {
            this.emitChange('add');
        }
    }

    undo() {
        if (this.index > 0) {
            this.index--;
            this.emitChange('undo');
        }
    }

    redo() {
        if (this.history[this.index + 1] && this.index < this.limit) {
            this.index++;
            this.emitChange('redo');
        }
    }

    emitChange(type: HistoryChangeType) {
        this.onChangeSubject.next({
            currentValue: this.history[this.index],
            canUndo: this.index > 0,
            canRedo: this.index < this.history.length - 1,
            index: this.index,
            type,
        });
    }
}
